import type { AdminOrderResponse } from '@inflip/contracts/admin';
import type { LicenseAmendment, LicenseAmendmentTerms } from '@inflip/contracts/user';
import {
  addContractMonths,
  joinNoteItems,
  LICENSE_AMENDMENT_STATUS_LABELS,
  LicenseAmendmentStatus,
  LicenseStatus,
  lastUseDateOf,
  MAX_NOTE_ITEM_LENGTH,
  MAX_NOTE_ITEMS,
  noteItemsOf,
  OrderStatus,
  startOfZonedDate,
  zonedDateOf,
} from '@inflip/shared';
import { useId, useState } from 'react';

import { NoteItems } from './note-items';
import { Button, Chip, ConfirmDialog, Dialog, DialogFooter, ErrorBanner, inputCls } from './ui';
import { api } from '../lib/api-client';
import { formatDateTime } from '../lib/time-zone';

/** 수정할 수 있는 주문 상태 — 서버(AMENDABLE_ORDER_STATUSES)와 같다 */
const AMENDABLE: string[] = [
  OrderStatus.LICENSE_ISSUED,
  OrderStatus.SETTLEMENT_PENDING,
  OrderStatus.PAYOUT_COMPLETED,
  OrderStatus.DISPUTED,
];

const STATUS_CLS: Record<LicenseAmendmentStatus, string> = {
  PENDING: 'text-amber-600',
  APPLIED: 'text-emerald-600',
  DECLINED: 'text-rose-600',
  CANCELLED: 'text-muted-foreground',
};

const dotDate = (d: string) => `${d.replaceAll('-', '. ')}.`;

/**
 * 라이선스 수정 — 관리자가 제안하고 구매자·판매자가 모두 동의해야 반영된다 (대리 발급은 없다).
 * 동의 대기 제안은 양쪽 동의 상태와 철회, 지난 제안은 누가 언제 동의·거절했는지 이력으로 보여 준다.
 */
export function LicenseAmendmentSection({
  order,
  token,
  onChanged,
}: {
  order: AdminOrderResponse;
  token: string;
  onChanged: () => Promise<unknown>;
}) {
  const [proposing, setProposing] = useState(false);
  const [cancelling, setCancelling] = useState<LicenseAmendment | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const license = order.license;
  if (!license) return null;

  const amendments = order.licenseAmendments ?? [];
  const open = amendments.find((a) => a.status === LicenseAmendmentStatus.PENDING) ?? null;
  const history = amendments.filter((a) => a.status !== LicenseAmendmentStatus.PENDING);
  const amendable = license.status === LicenseStatus.ACTIVE && AMENDABLE.includes(order.status);

  async function cancel(a: LicenseAmendment) {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await api.cancelLicenseAmendment(token, a.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
    // 지금 상태가 그려질 때까지 버튼을 막아 둔다
    await onChanged();
    setCancelling(null);
    setBusy(false);
  }

  return (
    <div className="py-3 text-sm">
      <div className="flex items-center justify-between gap-3">
        <p className="text-muted-foreground">라이선스 수정 (양쪽 동의)</p>
        {amendable && !open && (
          <Button size="sm" variant="secondary" onClick={() => setProposing(true)}>
            수정 제안
          </Button>
        )}
      </div>
      {error && (
        <div className="mt-2">
          <ErrorBanner message={error} />
        </div>
      )}

      {open && (
        <div className="mt-2 rounded-lg bg-amber-50/70 p-3.5">
          <div className="flex items-center justify-between gap-2">
            <p className="font-bold">
              동의 대기{' '}
              <span className="font-medium text-muted-foreground">
                · {formatDateTime(open.createdAt)} 제안
              </span>
            </p>
            <Button
              size="sm"
              variant="secondary"
              disabled={busy}
              onClick={() => setCancelling(open)}
            >
              제안 철회
            </Button>
          </div>
          <AmendmentChanges amendment={open} timeZone={order.timeZone} />
          <p className="mt-2 text-xs text-muted-foreground">사유: {open.reason}</p>
          <p className="mt-1 text-xs">
            구매자 <AgreeMark at={open.buyerAgreedAt} /> · 판매자{' '}
            <AgreeMark at={open.sellerAgreedAt} />
          </p>
        </div>
      )}
      {!open && !amendable && history.length === 0 && (
        <p className="mt-1.5 text-xs text-muted-foreground">
          사용 중인 라이선스만 수정할 수 있어요.
        </p>
      )}

      {history.length > 0 && (
        <ul className="mt-2 divide-y divide-border/60">
          {history.map((a) => (
            <li key={a.id} className="py-2.5">
              <p className="text-xs">
                <span className={`font-bold ${STATUS_CLS[a.status]}`}>
                  {LICENSE_AMENDMENT_STATUS_LABELS[a.status]}
                </span>{' '}
                <span className="text-muted-foreground">
                  · 제안 {formatDateTime(a.createdAt)}
                  {a.buyerAgreedAt && ` · 구매자 동의 ${formatDateTime(a.buyerAgreedAt)}`}
                  {a.sellerAgreedAt && ` · 판매자 동의 ${formatDateTime(a.sellerAgreedAt)}`}
                  {a.appliedAt && ` · 반영 ${formatDateTime(a.appliedAt)}`}
                  {a.declinedAt &&
                    ` · ${a.declinedBy === 'BUYER' ? '구매자' : '판매자'} 거절 ${formatDateTime(a.declinedAt)}`}
                  {a.cancelledAt && ` · 철회 ${formatDateTime(a.cancelledAt)}`}
                </span>
              </p>
              <AmendmentChanges amendment={a} timeZone={order.timeZone} />
              <p className="mt-1 text-xs text-muted-foreground">
                사유: {a.reason}
                {a.declineReason && ` · 거절 사유: ${a.declineReason}`}
              </p>
            </li>
          ))}
        </ul>
      )}

      {proposing && (
        <ProposeAmendmentDialog
          order={order}
          token={token}
          onDone={async () => {
            await onChanged();
            setProposing(false);
          }}
          onClose={() => setProposing(false)}
        />
      )}
      {cancelling && (
        <ConfirmDialog
          title="수정 제안을 철회할까요?"
          description="구매자·판매자에게 철회를 알려요. 라이선스는 그대로예요."
          confirmLabel="철회"
          danger
          busy={busy}
          onConfirm={() => void cancel(cancelling)}
          onClose={() => setCancelling(null)}
        />
      )}
    </div>
  );
}

function AgreeMark({ at }: { at: string | null }) {
  return at ? (
    <span className="font-semibold text-emerald-600">동의 {formatDateTime(at)}</span>
  ) : (
    <span className="font-semibold text-amber-600">대기</span>
  );
}

function lastUse(t: LicenseAmendmentTerms, timeZone: string): string {
  return t.expiresAt ? dotDate(lastUseDateOf(new Date(t.expiresAt), timeZone)) : '기한 없음';
}

/** 바뀌는 항목만 — 이전 → 이후 (계약 시간대 날짜) */
function AmendmentChanges({
  amendment,
  timeZone,
}: {
  amendment: LicenseAmendment;
  timeZone: string;
}) {
  const { before, after } = amendment;
  const startOf = (t: LicenseAmendmentTerms) =>
    dotDate(zonedDateOf(new Date(t.startsAt), timeZone));
  return (
    <div className="mt-1.5 space-y-0.5 text-xs">
      {startOf(before) !== startOf(after) && (
        <p>
          사용 시작일 {startOf(before)} → <b>{startOf(after)}</b>
        </p>
      )}
      {before.expiresAt !== after.expiresAt && (
        <p>
          마지막 사용일 {lastUse(before, timeZone)} → <b>{lastUse(after, timeZone)}</b>
          {amendment.expiresAtManual ? ' (직접 정함)' : ' (계약 기간대로)'}
        </p>
      )}
      {before.purchaseConfirmDueAt &&
        after.purchaseConfirmDueAt &&
        before.purchaseConfirmDueAt !== after.purchaseConfirmDueAt && (
          <p>
            구매확정 기한 {formatDateTime(before.purchaseConfirmDueAt, timeZone)} →{' '}
            <b>{formatDateTime(after.purchaseConfirmDueAt, timeZone)}</b>
          </p>
        )}
      {before.specialTerms !== after.specialTerms && (
        <div className="grid gap-2 pt-1 sm:grid-cols-2">
          <div>
            <p className="text-muted-foreground">추가 합의 사항 (지금)</p>
            {before.specialTerms ? (
              <NoteItems text={before.specialTerms} className="mt-0.5" />
            ) : (
              '없음'
            )}
          </div>
          <div>
            <p className="text-muted-foreground">추가 합의 사항 (바뀐 뒤)</p>
            {after.specialTerms ? (
              <NoteItems text={after.specialTerms} className="mt-0.5 font-medium" />
            ) : (
              '없음'
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * 수정 제안 창 — 지금 값으로 채워 두고 바꿀 것만 고친다.
 * 만료일은 계약 기간대로 시작일을 따라가거나, 마지막 사용일을 직접 정한다.
 */
function ProposeAmendmentDialog({
  order,
  token,
  onDone,
  onClose,
}: {
  order: AdminOrderResponse;
  token: string;
  onDone: () => Promise<unknown>;
  onClose: () => void;
}) {
  const license = order.license!;
  const tz = order.timeZone;
  const ids = { start: useId(), last: useId(), reason: useId() };
  const months = order.terms?.durationMonths ?? null;
  const [startsOn, setStartsOn] = useState(zonedDateOf(new Date(license.startsAt), tz));
  const [expiryMode, setExpiryMode] = useState<'FOLLOW' | 'MANUAL'>('FOLLOW');
  const [lastUseOn, setLastUseOn] = useState(
    license.expiresAt ? lastUseDateOf(new Date(license.expiresAt), tz) : '',
  );
  const [items, setItems] = useState<string[]>(() => {
    const list = noteItemsOf(license.specialTerms);
    return list.length > 0 ? list : [''];
  });
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 계약 기간대로 따라갈 때의 마지막 사용일 미리보기 (서버와 같은 계산)
  const followLastUse = (() => {
    if (!months || !startsOn) return null;
    const start =
      startsOn === zonedDateOf(new Date(license.startsAt), tz)
        ? new Date(license.startsAt)
        : startOfZonedDate(startsOn, tz);
    return lastUseDateOf(addContractMonths(start, months, tz), tz);
  })();

  async function submit() {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await api.proposeLicenseAmendment(token, order.id, {
        startsOn,
        expiryMode,
        lastUseOn: expiryMode === 'MANUAL' ? lastUseOn : undefined,
        specialTerms: joinNoteItems(items) || undefined,
        reason: reason.trim(),
        seenLicenseUpdatedAt: license.updatedAt,
      });
      // 지금 상태가 그려진 뒤에 창을 닫는다
      await onDone();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setBusy(false);
    }
  }

  const invalid =
    !startsOn ||
    reason.trim().length < 5 ||
    (expiryMode === 'MANUAL' && (!lastUseOn || lastUseOn < startsOn));

  return (
    <Dialog label="라이선스 수정 제안" title="라이선스 수정 제안" onClose={onClose} wide>
      <p className="text-sm text-muted-foreground">
        구매자·판매자에게 알림과 채팅으로 동의를 요청해요. 두 사람 모두 동의해야 라이선스와 증서가
        바뀌어요. 날짜는 계약 시간대 기준이에요.
      </p>

      <div className="mt-5 space-y-5">
        <section>
          <label htmlFor={ids.start} className="text-sm font-bold">
            사용 시작일
          </label>
          <input
            id={ids.start}
            type="date"
            value={startsOn}
            onChange={(e) => setStartsOn(e.target.value)}
            className={`mt-2 block w-full ${inputCls}`}
          />
        </section>

        <section>
          <p className="text-sm font-bold">만료일</p>
          <div className="mt-2 flex flex-wrap gap-1.5">
            <Chip active={expiryMode === 'FOLLOW'} onClick={() => setExpiryMode('FOLLOW')}>
              계약 기간대로
            </Chip>
            <Chip active={expiryMode === 'MANUAL'} onClick={() => setExpiryMode('MANUAL')}>
              직접 정하기
            </Chip>
          </div>
          {expiryMode === 'FOLLOW' ? (
            <p className="mt-2 text-xs text-muted-foreground">
              {months
                ? `시작일부터 ${months}개월 — 마지막 사용일 ${followLastUse ? dotDate(followLastUse) : '-'}`
                : '기간 없는 계약이라 만료일이 없어요.'}
            </p>
          ) : (
            <label htmlFor={ids.last} className="mt-2 block text-xs text-muted-foreground">
              마지막 사용일 (이날까지 사용)
              <input
                id={ids.last}
                type="date"
                value={lastUseOn}
                min={startsOn}
                onChange={(e) => setLastUseOn(e.target.value)}
                className={`mt-1 block w-full ${inputCls}`}
              />
            </label>
          )}
        </section>

        <section>
          <p className="text-sm font-bold">추가 합의 사항</p>
          <div className="mt-2 space-y-1.5">
            {items.map((item, i) => (
              <div key={i} className="flex items-center gap-1.5">
                <span className="w-5 shrink-0 text-right text-xs tabular-nums text-muted-foreground">
                  {i + 1}.
                </span>
                <input
                  value={item}
                  maxLength={MAX_NOTE_ITEM_LENGTH}
                  onChange={(e) =>
                    setItems((prev) => prev.map((v, j) => (j === i ? e.target.value : v)))
                  }
                  className={`min-w-0 flex-1 ${inputCls}`}
                />
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() =>
                    setItems((prev) => (prev.length > 1 ? prev.filter((_, j) => j !== i) : ['']))
                  }
                >
                  삭제
                </Button>
              </div>
            ))}
          </div>
          {items.length < MAX_NOTE_ITEMS && (
            <Button
              size="sm"
              variant="secondary"
              className="mt-2"
              onClick={() => setItems((prev) => [...prev, ''])}
            >
              항목 추가
            </Button>
          )}
        </section>

        <section>
          <label htmlFor={ids.reason} className="text-sm font-bold">
            수정 사유
          </label>
          <textarea
            id={ids.reason}
            rows={3}
            maxLength={1000}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="예: 발급 때 시작일을 잘못 고름, 양측 채팅 합의 (10/17)"
            className={`mt-2 w-full resize-none py-2 ${inputCls}`}
          />
          <p className="mt-1 text-xs text-muted-foreground">
            구매자·판매자에게 그대로 보여요 (5자 이상).
          </p>
        </section>
      </div>

      <DialogFooter notice={error ? <ErrorBanner message={error} /> : undefined}>
        <Button variant="secondary" className="flex-1" onClick={onClose}>
          취소
        </Button>
        <Button className="flex-1" disabled={busy || invalid} onClick={() => void submit()}>
          {busy ? '보내는 중…' : '동의 요청 보내기'}
        </Button>
      </DialogFooter>
    </Dialog>
  );
}
